// Library Imports
import * as React from "react";
import { Grid, Link, TextField } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import AddIcon from "@mui/icons-material/Add";
// import { TextareaAutosize } from "@mui/material";
import NotesTemplateSelect from "./NotesTemplateSelect";

// header import
import { getHeaders } from "../utils/HeaderHelper";

// app setting imports
import { AuthenticationContext } from "../Context";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useFlash } from "../FlashContext";

interface Props {
  blocks: Array<any>;
  setBlocks: any;
  encounterStatus: string;
  encounterBillingId: string;
  setReloadLogger: any;
  readOnly: boolean;
  encounterId: string;
}

const EncounterNotesPanel: React.FC<Props> = (props: any) => {
  // authentication context
  const authenticationSetting = React.useContext(AuthenticationContext);

  // For field states
  const [panelExpanded, setPanelExpanded] = React.useState<boolean>(false);
  const [disabled, setDisabled] = React.useState<boolean>(true);
  const [addendumForm, setAddendumForm] = React.useState<boolean>(false);
  const [addendumNotes, setAddendumNotes] = React.useState<string>("");
  const [templateModalOpen, setTemplateModalOpen] =
    React.useState<boolean>(false);
  const [template, setTemplate] = React.useState<any>();
  const [allAddendumNotes, setAllAddendumNotes] = React.useState<any>([]);
  const [unsavedChanges, setUnsavedChanges] = React.useState<boolean>(false);
  const { showToast } = useFlash();

  React.useEffect(() => {
    getNotes();
  }, []);

  const getEncounterId = () => {
    const url_string = window.location.href;
    const url = new URL(url_string);
    return url.searchParams.get("encounter_id") || props.encounterId;
  };

  const getNotes = () => {
    const encounterId = getEncounterId();
    if (encounterId) {
      fetch(`/encounters/encounter_notes_panel_data/${encounterId}`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            showToast({ 
              title: "Encounter Notes Load Error", 
              text: result.error || "Failed to retrieve encounter notes.", 
              type: "error" 
            });
          } else {
            console.log(result);
            props.setBlocks(result?.resource?.notes?.[0]?.blocks || []);
            setAllAddendumNotes(result?.resource?.addendums);
          }
        })
        .catch((error) => {
          showToast({ 
            title: "Encounter Notes Load Error", 
            text: error.message || "Failed to retrieve encounter notes.", 
            type: "error" 
          });
        });
    }
  };

  React.useEffect(() => {
    if (props.encounterStatus == "pended") {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [props.encounterStatus]);

  const handleCancel = () => {
    setAddendumForm(false);
  };
  const handleSave = () => {
    fetch(`/encounters/add_billing_addendum/${props.encounterBillingId}`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        encounter_notes: {
          notes: addendumNotes,
        },
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          showToast({ 
            title: "Addendum Save Error", 
            text: result.error || "Failed to save addendum.", 
            type: "error" 
          });
        } else {
          setAllAddendumNotes(result.resource.addendums);
          setAddendumForm(false);
          setAddendumNotes("");
          props.setReloadLogger(Date.now().toString()); //Trigerring the logger reload
        }
      })
      .catch((error) => {
        showToast({ 
          title: "Addendum Save Error", 
          text: error.message || "Failed to save addendum.", 
          type: "error" 
        });
      });
  };

  const generatePDF = () => {
    const encounterId = getEncounterId();
    if (encounterId) {
      fetch(`/encounters/notes_pdf?encounter_id=${encounterId}`, {
        method: "Post",
        headers: {
          "content-type": "application/pdf",
          accept: "application/pdf",
          "X-CSRF-Token": authenticationSetting.csrfToken,
          "X-Frame-Options": "sameorigin",
          "X-XSS-Protection": "1; mode=block",
          "Referrer-Policy": "strict-origin-when-cross-origin",
          "Content-Security-Policy": "default-src 'self'",
        },
      })
        .then((result) => {
          // Check if the response is a valid PDF file
          const contentType = result.headers.get("content-type");
          if (contentType && contentType.indexOf("application/pdf") !== -1) {
            return result.blob();
          } else {
            throw new Error("The response is not a valid PDF file");
          }
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const date = new Date().toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          });
          a.download = `encounter_notes_report_${date}.pdf`;
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          // Clean up the URL object
          URL.revokeObjectURL(url);
        })
        .catch((error) => {
          showToast({ 
            title: "PDF Download Error", 
            text: error.message || "Failed to download PDF.", 
            type: "error" 
          });
        });
    }
  };

  const updateBlock = (order, newValue) => {
    let newBlocks = props.blocks;
    newBlocks[order - 1].note = newValue;
    props.setBlocks([...newBlocks]);
  };

  const checkTemplate = () => {
    const templateFlag = props.blocks.some(
      (item) => item.note !== null && item.note.trim() !== ""
    );
    if (templateFlag || template) {
      return true;
    } else {
      return false;
    }
  };

  React.useEffect(() => {
    template && props.setBlocks(template.blocks);
  }, [template]);

  return (
    <Grid container item xs={12} className="single-panel">
      <NotesTemplateSelect
        templateModalOpen={templateModalOpen}
        setTemplateModalOpen={setTemplateModalOpen}
        setTemplate={setTemplate}
        checkTemplate={checkTemplate}
        setUnsavedChanges={setUnsavedChanges}
        currentTemplate={template}
        currentBlocks={props.blocks}
      />
      <Grid item xs={12}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          className="admin-header"
        >
          <Grid item xs={11} lg={6}>
            <h3 style={{ display: "inline-block" }}>Encounter Notes</h3>
            {props.encounterStatus != "signed" && (
              <div
                className="action-container"
                style={{ display: "inline-block" }}
              >
                <Link
                  className="action-link"
                  onClick={() => setTemplateModalOpen(true)}
                >
                  <AddIcon className="plus-icon" />{" "}
                  <span className="">Add Template</span>
                </Link>
              </div>
            )}
          </Grid>
          <Grid
            container
            item
            xs={1}
            direction="row"
            justifyContent="flex-end"
            style={{ flexWrap: "nowrap" }}
          >
            {panelExpanded ? (
              <>
                <span>
                  <Link className="action-icon">
                    <ArrowDropUpIcon className="expand-icon" />
                  </Link>
                </span>
              </>
            ) : (
              <>
                <span>
                  <Link className="action-icon ">
                    <ArrowDropDownIcon className="expand-icon" />
                  </Link>
                </span>
              </>
            )}
          </Grid>
        </Grid>

        {props.blocks.map((block, index) => (
          <Grid key={index} container className="form-body notes-container">
            <ReactQuill
              theme="snow"
              value={block.note}
              onChange={(value) => updateBlock(block.order, value)}
              className="field field-2 note-field"
              readOnly={disabled}
              modules={{
                clipboard: {
                  matchVisual: false,
                },
              }}
            />
          </Grid>
        ))}

        {allAddendumNotes &&
          allAddendumNotes.map((addendum, index) => (
            <Grid
              item
              xs={12}
              className="field-container form-body"
              style={{ marginTop: "1rem" }}
            >
              <h5
                style={{ marginTop: "0px", marginBottom: "0px", color: "grey" }}
              >
                {" "}
                Addendum{" "}
              </h5>
              <TextField
                id="addendum-input"
                value={addendum}
                className="field field-2"
                required
                onChange={(event) => {
                  props.setAllAddendumNotes(event.target.value);
                }}
                disabled={disabled || props.readOnly}
                fullWidth
                multiline
              />
            </Grid>
          ))}

        <Grid container style={{ marginBottom: "20px" }}>
          {disabled && (
            <>
              {addendumForm ? (
                <Grid container className="form-body">
                  <Grid item xs={12} className="field-container">
                    <h5
                      style={{
                        marginTop: "0px",
                        marginBottom: "0px",
                        color: "grey",
                      }}
                    >
                      {" "}
                      Add Addendum{" "}
                    </h5>
                    <TextField
                      id="plan_name"
                      value={addendumNotes}
                      className="field field-2"
                      required
                      multiline
                      minRows={5}
                      onChange={(event) => {
                        setAddendumNotes(event.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid
                      container
                      className="button-container"
                      justifyContent="center"
                      spacing={1}
                    >
                      <Grid item xs={2}>
                        <div className="cancel-link-container">
                          <Link className="cancel-link" onClick={handleCancel}>
                            Cancel
                          </Link>
                        </div>
                      </Grid>
                      <Grid item className="save-btn-container" xs={2}>
                        <Link className="save-btn" onClick={handleSave}>
                          Save
                        </Link>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <div
                      className="action-container"
                      style={{ marginTop: "0px" }}
                    >
                      <Link
                        className="action-link"
                        onClick={() => setAddendumForm(true)}
                      >
                        <AddIcon className="plus-icon" />
                        <span>Add Addendum</span>
                      </Link>
                    </div>
                    {props.encounterStatus === "signed" && (
                      <Link
                        onClick={generatePDF}
                        className={
                          getEncounterId()
                            ? "basic-button generate-pdf orange-btn"
                            : "basic-button generate-pdf orange-btn disabled"
                        }
                      >
                        {" "}
                        Generate PDF Version{" "}
                      </Link>
                    )}
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EncounterNotesPanel;
